import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Space, message, Divider, Row, Col, Image, Spin, Typography } from "antd";
import { SaveOutlined, EditOutlined, CloseOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import updateSellerMutation from "../queries/updateSellerMutation";
import linkedOrgQuery from "../queries/linkedOrgQuery";

const { Text } = Typography;

const MarketplaceSellerGeneral = ({ seller, onUpdate }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [linkedOrg, setLinkedOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  // Fetch Linked Org
  useEffect(() => {
    const fetchLinkedOrg = async () => {
      if (!seller?.objectId) return;

      setOrgLoading(true);
      try {
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

        const response = await axios.post(
          graphqlUrl,
          {
            query: linkedOrgQuery,
            variables: { sellerId: seller.objectId },
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const orgs = response.data?.marketplace_orgs?.edges || [];
        if (orgs.length > 0) {
          setLinkedOrg(orgs[0].node);
        }
      } catch (error) {
        console.error("Error fetching linked org:", error);
      } finally {
        setOrgLoading(false);
      }
    };

    fetchLinkedOrg();
  }, [seller?.objectId, getTokenSilently, env]);

  // Initialize Form
  useEffect(() => {
    if (seller) {
      form.setFieldsValue({
        name: seller.name,
        phone: seller.phone,
        email: seller.email,
        website: seller.website,
        address: seller.address,
        city: seller.city,
        zip: seller.zip,
      });
    }
  }, [seller, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const fields = {
        name: values.name,
        phone: values.phone,
        email: values.email,
        website: values.website,
        address: values.address,
        city: values.city,
        zip: values.zip,
      };

      await axios.post(
        graphqlUrl,
        {
          query: updateSellerMutation,
          variables: { id: seller.objectId, fields },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Profilo venditore aggiornato con successo");
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating seller:", error);
      message.error("Errore durante l'aggiornamento del profilo venditore");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.setFieldsValue({
      name: seller.name,
      phone: seller.phone,
      email: seller.email,
      website: seller.website,
      address: seller.address,
      city: seller.city,
      zip: seller.zip,
    });
    setIsEditing(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Images Section */}
      {(seller.logo || seller.home_cover) && (
        <Card title="Immagini Profilo">
          <Space size="large" direction="horizontal" wrap>
            {seller.logo && (
              <div>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Logo</div>
                <Image
                  src={seller.logo.url}
                  alt="Logo"
                  width={200}
                  style={{ objectFit: "contain" }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                />
              </div>
            )}
            {seller.home_cover && (
              <div>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Copertina</div>
                <Image
                  src={seller.home_cover.url}
                  alt="Home Cover"
                  width={300}
                  style={{ objectFit: "contain" }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                />
              </div>
            )}
          </Space>
        </Card>
      )}

      {/* Main Form Section */}
      <Card 
        title="Dettagli Profilo Venditore" 
        extra={
          !isEditing ? (
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={() => setIsEditing(true)}
            >
              Modifica
            </Button>
          ) : (
            <Space>
              <Button 
                icon={<CloseOutlined />} 
                onClick={handleCancel}
              >
                Annulla
              </Button>
              <Button 
                type="primary" 
                icon={<SaveOutlined />} 
                loading={loading}
                onClick={() => form.submit()}
              >
                Salva
              </Button>
            </Space>
          )
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={!isEditing}
        >
          <Divider orientation="left">Informazioni Generali</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Slug">
                <Input value={seller.slug || "-"} disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Nome"
                name="name"
                rules={[{ required: true, message: "Il nome è obbligatorio" }]}
              >
                <Input placeholder="Nome del venditore" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Telefono"
                name="phone"
              >
                <Input placeholder="Numero di telefono" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: 'email', message: "Inserire un'email valida" }]}
              >
                <Input placeholder="Email aziendale" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Sito Web"
                name="website"
              >
                <Input placeholder="https://www.esempio.it" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Indirizzo</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Indirizzo"
                name="address"
              >
                <Input placeholder="Via Roma 1" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Città"
                name="city"
              >
                <Input placeholder="Roma" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="CAP"
                name="zip"
              >
                <Input placeholder="00100" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Collegamenti Organizzazioni</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Organizzazione FuneralCloud">
                {orgLoading ? (
                  <Spin size="small" />
                ) : linkedOrg ? (
                  <div>
                    <Link to={`/funeralcloud/orgs/${linkedOrg.objectId}/riepilogo`}>
                      {linkedOrg.name}
                    </Link>
                    {linkedOrg.city && <Text type="secondary" style={{ marginLeft: 8 }}>({linkedOrg.city})</Text>}
                  </div>
                ) : (
                  <Text type="secondary">Nessuna organizzazione collegata</Text>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Organizzazione Eterno">
                 {seller.org_pointer ? (
                  <Link to={`/orgs/${seller.org_pointer.objectId}`}>
                    {seller.org_pointer.nome} ({seller.org_pointer.objectId})
                  </Link>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default MarketplaceSellerGeneral;
